import { useEffect } from 'react';
import { useServices, useSiteContext } from '@wix/app-market-core';
import { useQuery } from '@tanstack/react-query';
import type { Offer } from '@wix/app-market-services';
import { EventType } from '@wix/app-market-services';

const WIX_CAPITAL_DEALER_PLACEMENT_ID = '76e38d60-6e86-4ae4-a691-a3709c270f74';
const WIX_CAPITAL_DEALER_OFFERING_ID = 'b4927fbb-492a-427f-9a1f-fdcb4840757c';

const isOfferExpired = (offer?: Offer) => {
  const hasEndDate = Boolean(offer?.endDate);
  const now = new Date();
  return hasEndDate && now > new Date(offer!.endDate!);
};

export function useWixCapitalDealerBanner() {
  const services = useServices();
  const { metaSiteId } = useSiteContext();

  const { data: offers } = useQuery({
    queryKey: ['dealer-offers-home-page'],
    queryFn: async () => {
      try {
        const response = await services.dealerServingOffer.listOffers({
          realEstateId: WIX_CAPITAL_DEALER_PLACEMENT_ID,
        });
        return response.offers ?? [];
      } catch {
        return [];
      }
    },
  });

  const wixCapitalDealerOffer = offers?.find(
    (offer) =>
      offer.offerGuid === WIX_CAPITAL_DEALER_OFFERING_ID &&
      !isOfferExpired(offer),
  );

  const offerAssets = wixCapitalDealerOffer?.asset?.creative;

  useEffect(() => {
    if (offerAssets) {
      void services.dealerOfferEvent.reportEvent({
        event: {
          type: EventType.VIEW,
          offerId: wixCapitalDealerOffer.offerGuid,
          realEstateId: WIX_CAPITAL_DEALER_PLACEMENT_ID,
        },
      });
    }
  }, [offerAssets]);

  const parsedCtaUrl = offerAssets?.ctaUrl?.includes('{MSID}')
    ? offerAssets?.ctaUrl.replace('{MSID}', metaSiteId)
    : offerAssets?.ctaUrl;

  const onPrimaryCtaClick = () => {
    void services.dealerOfferEvent.reportEvent({
      event: {
        type: EventType.MAIN_CTA_CLICK,
        offerId: wixCapitalDealerOffer?.offerGuid,
        realEstateId: WIX_CAPITAL_DEALER_PLACEMENT_ID,
        mainCtaClick: parsedCtaUrl ? { targetUrl: parsedCtaUrl } : undefined,
      },
    });
  };

  return {
    assets: wixCapitalDealerOffer?.asset?.creative,
    parsedCtaUrl,
    onPrimaryCtaClick,
  } as const;
}
